import { useCallback, useEffect, useId, useRef, type RefObject } from 'react';

type UseModalMenuA11yParams = {
  isOpen: boolean;
  onClose: () => void;
};

type UseModalMenuA11yResult = {
  /** メニュー本体に付与する id。トリガーの aria-controls と対応させる */
  menuId: string;
  menuRef: RefObject<HTMLDivElement>;
  /** 開閉トリガー。閉じたときにフォーカスを戻す先 */
  triggerRef: RefObject<HTMLButtonElement>;
};

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * モーダル的に開くメニューのアクセシビリティを担う。
 * - 開いている間は背景をスクロールさせない（スクロールバー分の padding で表示ずれを防ぐ）
 * - Escape で閉じる
 * - Tab / Shift+Tab のフォーカスをメニュー内で循環させる
 * - 閉じたときにトリガーへフォーカスを戻す
 */
export const useModalMenuA11y = ({ isOpen, onClose }: UseModalMenuA11yParams): UseModalMenuA11yResult => {
  const menuId = useId();
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const wasOpenRef = useRef(false);

  // 背景のスクロール抑止。スクロールバーが消える分の幅を padding で補い、
  // 開閉のたびにレイアウトが横にずれるのを防ぐ
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const { body } = document;
    const previousOverflow = body.style.overflow;
    const previousPaddingRight = body.style.paddingRight;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

    body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) {
      body.style.paddingRight = `${scrollbarWidth}px`;
    }

    return () => {
      body.style.overflow = previousOverflow;
      body.style.paddingRight = previousPaddingRight;
    };
  }, [isOpen]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }

      if (event.key !== 'Tab' || !menuRef.current) {
        return;
      }

      const focusables = [...menuRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)].filter(
        (element) => element.offsetParent !== null
      );

      if (focusables.length === 0) {
        return;
      }

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const { activeElement } = document;

      if (event.shiftKey && activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
    [onClose]
  );

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, handleKeyDown]);

  // 開いた直後はメニュー内の先頭へ、閉じた直後はトリガーへフォーカスを移す
  useEffect(() => {
    if (isOpen) {
      const firstFocusable = menuRef.current?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      firstFocusable?.focus();
      wasOpenRef.current = true;
      return;
    }

    if (wasOpenRef.current) {
      triggerRef.current?.focus();
      wasOpenRef.current = false;
    }
  }, [isOpen]);

  return { menuId, menuRef, triggerRef };
};
